import { Link, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { useEffect, useState } from "react";

import iconLogout from "../assets/svgs/icon-logout-white.svg";
import jiraLogo from "../assets/svgs/jira-logo.svg";
import { constants } from "../services/constants/JIRA I_Issue_Collector";
import { useAuth } from "../contexts/AuthContext";
import GlobalDashboardButton from "../components/GlobalDashboardButton";
import {
  NavItem,
  navItems,
  doesRouteMatch,
  findActiveParent,
  findChildByUrl,
  findRouteRequirement,
} from "./navConfig";

interface SidebarProps {
  user: {
    name: string;
    occupation: string;
  };
  drawerWidth: number;
  closedDrawerWidth: number;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  isNonMobile: boolean;
}

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
  drawerWidth,
  closedDrawerWidth,
}: SidebarProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, hasPermission, isAdmin } = useAuth();
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(() =>
    findActiveParent(pathname),
  );
  const [flyoutMenu, setFlyoutMenu] = useState<string | null>(null);

  useEffect(() => {
    setExpandedMenu(findActiveParent(pathname));
    setFlyoutMenu(null);
  }, [pathname]);

  useEffect(() => {
    setFlyoutMenu(null);
  }, [isSidebarOpen]);

  // Helper function to check if user has permission for a route
  const hasRoutePermission = (url: string): boolean => {
    // Admin users have access to all routes
    if (isAdmin) return true;

    // Normalize URL to start with /
    const normalizedUrl = url.startsWith("/") ? url : `/${url}`;

    // Check requiredAnyPermission first (OR logic — show if ANY permission matches)
    const child = findChildByUrl(normalizedUrl);
    if (
      child?.requiredAnyPermission &&
      child.requiredAnyPermission.length > 0
    ) {
      return child.requiredAnyPermission.some((p) =>
        hasPermission(p.entity, p.action),
      );
    }

    // Fall back to single requiredPermission
    const requirement = findRouteRequirement(normalizedUrl);

    // If no requirement found, allow access (public route)
    if (!requirement) return true;

    // Check if user has the required permission
    return hasPermission(requirement.entity, requirement.action);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Box
      component="nav"
      className={
        isSidebarOpen
          ? "position-relative"
          : "position-relative sidebar-drawer-closed"
      }
    >
      <Box
        sx={{
          width: `${
            isSidebarOpen
              ? drawerWidth + "px"
              : !isNonMobile
                ? "13px"
                : closedDrawerWidth + "px"
          }`,
        }}
        className={
          isSidebarOpen && !isNonMobile
            ? "small-screen-navbar"
            : isSidebarOpen
              ? "sidebar-drawer"
              : "side-bar-open sidebar-drawer"
        }
      >
        <Box
          className={`MuiDrawer-paper ${
            !isNonMobile ? "menu-in-small-screen-parent" : ""
          }`}
          sx={{
            width: `${
              isSidebarOpen
                ? drawerWidth + "px"
                : !isNonMobile
                  ? "13px"
                  : closedDrawerWidth + "px"
            }`,
          }}
        >
          <IconButton
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="navbar-icon"
            aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isSidebarOpen ? (
              <ChevronLeftRoundedIcon className="sidebar-toggle-icon" />
            ) : (
              <MenuRoundedIcon className="sidebar-toggle-icon" />
            )}
          </IconButton>
          <Box
            width="100%"
            height="100%"
            className={!isNonMobile ? "menu-in-small-screen" : ""}
          >
            <ul className="list-name">
              {(() => {
                const filteredNavItems = navItems.filter((navItem) => {
                  // Filter children based on permissions
                  const filteredChildren = navItem.children?.filter((child) =>
                    hasRoutePermission(child.url),
                  );

                  // If this nav item has children, only show it if at least one child is accessible
                  // If it has no children, check permission for the nav item itself
                  return navItem.children
                    ? filteredChildren && filteredChildren.length > 0
                    : navItem.url
                      ? hasRoutePermission(navItem.url)
                      : true; // Show items without URLs (like category headers)
                });

                // Show empty state message if no items are visible
                if (filteredNavItems.length === 0) {
                  return (
                    <li className="empty-state">
                      <span className="without-link no-menu">
                        No Menu Items Available
                      </span>
                    </li>
                  );
                }

                return filteredNavItems.map(
                  (navItem: NavItem, index: number) => {
                    // Filter children based on permissions for rendering
                    const filteredChildren = navItem.children?.filter((child) =>
                      hasRoutePermission(child.url),
                    );

                    const { text, url, icon, isStroke, routes } = navItem;
                    const children = filteredChildren;
                    const allRoutes = children
                      ? children.flatMap((child) => child.routes || [])
                      : routes || [];
                    const normalizedUrl = url
                      ? url.startsWith("/")
                        ? url
                        : `/${url}`
                      : undefined;
                    const isActive = allRoutes.some((route) =>
                      doesRouteMatch(pathname, route),
                    );
                    const isExpanded =
                      !!children && isSidebarOpen && expandedMenu === text;
                    const isFlyoutOpen =
                      !!children && !isSidebarOpen && flyoutMenu === text;
                    const isSubmenuVisible = isExpanded || isFlyoutOpen;
                    const menuId = text
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/(^-|-$)/g, "");

                    return (
                      <li
                        key={text}
                        className={`${isStroke ? "icon-stroke" : ""} ${
                          children ? "has-children" : ""
                        } ${isExpanded ? "Mui-expanded" : ""} ${
                          isFlyoutOpen ? "is-flyout-open" : ""
                        } menu-${index + 1} ${isActive ? "active" : ""}`}
                        onMouseEnter={() => {
                          if (children && !isSidebarOpen) setFlyoutMenu(text);
                        }}
                        onMouseLeave={() => {
                          if (!isSidebarOpen) setFlyoutMenu(null);
                        }}
                        onFocusCapture={() => {
                          if (children && !isSidebarOpen) setFlyoutMenu(text);
                        }}
                        onBlurCapture={(event) => {
                          const nextTarget = event.relatedTarget;
                          if (
                            !isSidebarOpen &&
                            (!nextTarget ||
                              !event.currentTarget.contains(nextTarget as Node))
                          ) {
                            setFlyoutMenu(null);
                          }
                        }}
                      >
                        <ListItemIcon>{icon}</ListItemIcon>
                        <span className="list-item-name">
                          {children ? (
                            <button
                              type="button"
                              id={`${menuId}-trigger`}
                              className="without-link menu-parent-trigger"
                              aria-expanded={isSubmenuVisible}
                              aria-controls={`${menuId}-submenu`}
                              onClick={() => {
                                if (isSidebarOpen) {
                                  setExpandedMenu((current) =>
                                    current === text ? null : text,
                                  );
                                } else {
                                  setFlyoutMenu((current) =>
                                    current === text ? null : text,
                                  );
                                }
                              }}
                            >
                              <span className="menu-label">{text}</span>
                              <ChevronRightRoundedIcon
                                className="menu-chevron"
                                aria-hidden="true"
                              />
                            </button>
                          ) : normalizedUrl ? (
                            <Link to={normalizedUrl}>
                              <span className="menu-label">{text}</span>
                            </Link>
                          ) : (
                            <span className="without-link">
                              <span className="menu-label">{text}</span>
                            </span>
                          )}
                          {children && isSubmenuVisible && (
                            <ul
                              id={`${menuId}-submenu`}
                              className={`sub-menu ${
                                isFlyoutOpen ? "sub-menu-flyout" : ""
                              }`}
                              aria-labelledby={`${menuId}-trigger`}
                            >
                              {children.map((subItem) => {
                                const isChildActive = (
                                  subItem.routes || []
                                ).some((route) =>
                                  doesRouteMatch(pathname, route),
                                );

                                return (
                                  <li
                                    key={subItem.text}
                                    className={
                                      isChildActive ? "active-child" : ""
                                    }
                                  >
                                    <Link to={subItem.url}>
                                      <span
                                        className="sub-menu-icon"
                                        aria-hidden="true"
                                      >
                                        {subItem.icon}
                                      </span>
                                      <span className="sub-menu-label">
                                        {subItem.text}
                                      </span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </span>
                      </li>
                    );
                  },
                );
              })()}
            </ul>

            <Box className="logout-box">
              {/* Dashboard Button - Admin Only */}
              {isAdmin && (
                <Box
                  className="jira-feedback dashboard-button cursor-pointer"
                  onClick={() => setIsDashboardOpen(true)}
                >
                  <MonitorHeartIcon
                    className="dashboard-icon"
                    sx={{ width: 24, height: 24 }}
                  />
                  Grafana Dashboard
                </Box>
              )}

              {/* Jira Feedback */}
              <Box
                className="jira-feedback cursor-pointer"
                onClick={() => {
                  const blankWindow = window.open("Feedback");
                  const document = blankWindow?.document;
                  if (document) {
                    document.open();
                    document.write(`
                    <html>
                      <head>
                        <title>Feedback</title>
                      </head>
                      <body>
                        ${constants.JIRA_ISSUE_COLLECTOR_SCRIPT}
                      </body>
                    </html>
                  `);
                    document.close();
                  }
                }}
              >
                <img src={jiraLogo} alt="" />
                Jira Feedback
              </Box>

              {/* BUG-20: Logout button removed from sidebar - only in user dropdown menu
              <button className="btn-logout" onClick={handleLogout}>
                <img src={iconLogout} alt="" />
                <span>Logout</span>
              </button>
              */}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dashboard Drawer - Controlled from sidebar button */}
      <GlobalDashboardButton
        isOpen={isDashboardOpen}
        onClose={() => setIsDashboardOpen(false)}
        showFloatingButton={false}
      />
    </Box>
  );
};

export default Sidebar;
